<template>
  <EChartWrapper v-if="!loading" ref="chartRef" :options="donutChartOptions" :height="info.height"></EChartWrapper>
  <ChartLoading v-else :height="info.height"></ChartLoading>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { getChartColors } from "@/services/ChartsServices";
import { usePropsStore } from "@/stores/props";
import { formatCurrency } from "@/services/Utility";
import type { PieChartInfo } from "@/classes/ChartInfo";

interface Props {
  info: PieChartInfo;
}

const props = defineProps<Props>();
const propsStore = usePropsStore();
const chartRef = ref();
const loading = ref(true);

const showLoading = (): void => {
  loading.value = true;
};

const loadingComplete = (): void => {
  nextTick(() => (loading.value = false));
};

defineExpose({ showLoading, loadingComplete });

const colors = ref<Array<Array<string>>>(getChartColors());
watch(
  () => propsStore.theme,
  (theme) => {
    colors.value = getChartColors();
  }
);
const data = computed(() => {
  return props.info.data.map((value: number, index: number) => {
    const normalColor = colors.value[0][index % colors.value[0].length];
    const emphasisColor = colors.value[1][index % colors.value[1].length];

    return {
      value: value,
      name: props.info.labels[index],
      itemStyle: {
        color: normalColor,
      },
      emphasis: {
        itemStyle: {
          color: emphasisColor,
        },
        lineStyle: {
          color: emphasisColor,
        },
      },
    };
  });
});
const totals = computed<number>(() => props.info.data.reduce((accumulator: number, amt: number) => accumulator + amt, 0));

const donutChartOptions = computed<any>(() => {
  return {
    legend: {
      show: true,
    },
    title: {
      text: props.info.title,
      subtext: props.info.currency ? formatCurrency(totals.value) : totals.value,
      textStyle: {
        fontSize: 15,
      },
      subtextStyle: {
        fontSize: props.info.currency ? 20 : 30,
        lineHeight: 20,
      },
      left: props.info.titelLeftPosition,
      top: props.info.titleTopPosition,
    },
    tooltip: {
      trigger: "item",
      valueFormatter: (value: any) => (props.info.currency ? formatCurrency(value) : value),
    },
    series: [
      {
        type: "pie",
        data: data.value,
        label: {
          show: false,
        },
        radius: props.info.donut ? ["40%", "75%"] : ["75%"],
        center: ["50%", "40%"],
      },
    ],
  };
});
</script>
